import { Link } from "react-router-dom";
import type { Film } from "@/mocks/films";
import { typeLabel } from "@/utils/film";

interface FilmCardProps {
  film: Film;
  favorited?: boolean;
  onToggleFavorite?: (id: string) => void;
}

export default function FilmCard({ film, favorited, onToggleFavorite }: FilmCardProps) {
  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/detail/${film.id}`} className="relative block">
        <div className="relative h-64 w-full overflow-hidden bg-background-200">
          <img
            src={film.poster}
            alt={`${film.title} 海报`}
            title={`${film.title} - 伦理影视作品`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute left-2.5 top-2.5 flex items-center gap-1.5">
            <span className="rounded-md bg-primary-500/95 px-2 py-0.5 text-xs font-medium text-background-50">
              {typeLabel(film.type)}
            </span>
            {film.year > 0 && (
              <span className="rounded-md bg-foreground-950/70 px-2 py-0.5 text-xs text-background-50">
                {film.year}
              </span>
            )}
          </div>
          <div className="absolute right-2.5 top-2.5 flex items-center gap-1 rounded-md bg-background-50/95 px-1.5 py-0.5">
            <i className="ri-star-fill text-xs text-accent-500"></i>
            <span className="text-xs font-semibold text-foreground-900">{film.rating.toFixed(1)}</span>
          </div>
        </div>
      </Link>

      <div className="flex flex-1 flex-col p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-heading text-base font-semibold leading-snug text-foreground-950">
            <Link to={`/detail/${film.id}`} className="transition-colors hover:text-primary-700">
              {film.title}
            </Link>
          </h3>
          {onToggleFavorite && (
            <button
              type="button"
              onClick={() => onToggleFavorite(film.id)}
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors ${
                favorited
                  ? "bg-accent-50 text-accent-600"
                  : "text-secondary-400 hover:bg-background-100 hover:text-accent-500"
              }`}
              aria-label={favorited ? "取消收藏" : "收藏"}
            >
              <i className={favorited ? "ri-heart-3-fill" : "ri-heart-3-line"}></i>
            </button>
          )}
        </div>

        <p className="mt-1 text-xs text-secondary-500">
          {film.region} · {film.runtime}
        </p>

        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {film.genres.slice(0, 3).map((g) => (
            <span
              key={g}
              className="rounded-md bg-secondary-100 px-1.5 py-0.5 text-[11px] text-secondary-700"
            >
              {g}
            </span>
          ))}
        </div>

        <p className="mt-3 line-clamp-2 text-xs leading-relaxed text-foreground-600">
          {film.synopsis}
        </p>

        <p className="mt-3 text-xs text-secondary-500">
          导演 {film.director} · 主演 {film.cast.slice(0, 2).join(" / ")}
        </p>
      </div>
    </article>
  );
}